import React, { useState, useEffect, useCallback } from "react";
import { ThemeProvider, CssBaseline, Box, Container, Alert, Snackbar } from "@mui/material";
import theme from "./theme";
import AuthScreen from "./components/AuthScreen";
import Sidebar from "./components/Sidebar";
import HubModal from "./components/HubModal";
import ConnectionsList from "./components/ConnectionsList";
import VoIPPanel from "./components/VoIPPanel";
import DashboardPage from "./pages/DashboardPage";
import HubsPage from "./pages/HubsPage";
import LogsPage from "./pages/LogsPage";
import BillingPage from "./pages/BillingPage";
import WhiteLabelPage from "./pages/WhiteLabelPage";
import SettingsPage from "./pages/SettingsPage";
import TenantsPage from "./pages/TenantsPage";
import ConnectionsAdminPage from "./pages/ConnectionsAdminPage";
import ConnectPage from "./pages/ConnectPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import { hubApi } from "./services/hubApi";
import { connectSocket, disconnectSocket } from "./services/socket";

const SIDEBAR_WIDTH = 240;

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [connections, setConnections] = useState([]);
  const [hubOpen, setHubOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [voipPanel, setVoipPanel] = useState(null);
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadConnections = useCallback(async () => {
    try {
      const { data } = await hubApi.getConnections();
      setConnections(data.connections || []);
    } catch (err) {
      console.error("Error loading connections:", err);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const apiKey = localStorage.getItem("hub_api_key");
    const token = localStorage.getItem("hub_token");

    if (!apiKey && !token) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await hubApi.getLicense();
      setTenant(data.tenant);
      setPlan(data.plan);
      setAuthenticated(true);
      await loadConnections();
    } catch {
      localStorage.removeItem("hub_api_key");
      localStorage.removeItem("hub_token");
    } finally {
      setLoading(false);
    }
  }, [loadConnections]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!authenticated || !tenant) return;

    // Sem tenantId: o servidor decide a sala pela credencial (P0-A).
    const socket = connectSocket();

    socket.on("connection:created", (conn) => {
      showSnackbar(`${conn.name} conectado com sucesso!`);
      loadConnections();
    });
    socket.on("connection:updated", () => loadConnections());
    socket.on("connection:deleted", () => loadConnections());
    socket.on("plan:updated", (data) => {
      setPlan(data.plan);
      showSnackbar(`Canal ${data.channel} ${data.action === "activate" ? "ativado" : "desativado"}!`);
      checkAuth();
    });
    socket.on("connection:error", (data) => {
      showSnackbar(`Canal ${data.channel}: ${data.error}`, "error");
      loadConnections();
    });
    socket.on("call:incoming", (data) => showSnackbar(`Chamada entrante de ${data.from}`, "info"));
    socket.on("call:answered", () => {});
    socket.on("call:ended", (data) => {
      if (data.duration) showSnackbar(`Chamada finalizada (${Math.floor(data.duration / 60)}m${data.duration % 60}s)`, "info");
    });
    socket.on("call:missed", (data) => showSnackbar(`Chamada perdida de ${data.from || "desconhecido"}`, "warning"));

    return () => { disconnectSocket(); };
  }, [authenticated, tenant, loadConnections, showSnackbar, checkAuth]);

  const handleLogin = async (email, password) => {
    try {
      const { data: auth } = await hubApi.login(email, password);
      localStorage.setItem("hub_token", auth.token);
      localStorage.removeItem("hub_api_key");
      const { data } = await hubApi.getLicense();
      setTenant(data.tenant);
      setPlan(data.plan);
      setAuthenticated(true);
      await loadConnections();
      showSnackbar("Autenticado com sucesso!");
    } catch {
      localStorage.removeItem("hub_token");
      showSnackbar("Email ou senha invalidos", "error");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("hub_api_key");
    localStorage.removeItem("hub_token");
    setAuthenticated(false);
    setTenant(null);
    setConnections([]);
    setCurrentPage("dashboard");
    disconnectSocket();
  };

  const handleConnectionCreated = () => {
    loadConnections();
    setHubOpen(false);
  };

  const handleDisconnect = async (id) => {
    try {
      await hubApi.disconnectConnection(id);
      showSnackbar("Conexao desconectada");
      loadConnections();
    } catch (err) {
      showSnackbar(err.response?.data?.error || "Erro ao desconectar", "error");
    }
  };

  const handleReconnect = async (id) => {
    try {
      await hubApi.reconnectConnection(id);
      showSnackbar("Conexao reconectada!");
      loadConnections();
    } catch (err) {
      showSnackbar(err.response?.data?.error || "Erro ao reconectar", "error");
    }
  };

  const handleDelete = async (id) => {
    try {
      await hubApi.deleteConnection(id);
      showSnackbar("Conexao removida");
      loadConnections();
    } catch (err) {
      showSnackbar(err.response?.data?.error || "Erro ao remover", "error");
    }
  };

  const handleOpenVoIP = (connectionId) => {
    setVoipPanel({ connectionId });
  };

  // Rota standalone /connect: Embedded Signup hospedado (popup do HUB Local).
  if (typeof window !== "undefined" && window.location.pathname === "/connect") {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ConnectPage />
      </ThemeProvider>
    );
  }

  // Rota standalone /reset-password: redefinição de senha pelo link do e-mail (pública).
  if (typeof window !== "undefined" && window.location.pathname === "/reset-password") {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ResetPasswordPage />
      </ThemeProvider>
    );
  }

  // ── Loading ──
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#0B0F19" }}>
          <Box sx={{ color: "#6B7280" }}>Carregando...</Box>
        </Box>
      </ThemeProvider>
    );
  }

  // ── Auth ──
  if (!authenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthScreen onLogin={handleLogin} />
        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
        </Snackbar>
      </ThemeProvider>
    );
  }

  // ── Main App ──
  const renderPage = () => {
    if (voipPanel) {
      return <VoIPPanel connectionId={voipPanel.connectionId} onBack={() => setVoipPanel(null)} />;
    }

    switch (currentPage) {
      case "dashboard":
        return <DashboardPage />;
      case "connections":
        // Admin/Central: vista global com filtros (todos tenants)
        // Tenant comum: lista propria via ConnectionsList
        return <ConnectionsAdminPage showSnackbar={showSnackbar} />;
      case "hubs":
        return <HubsPage showSnackbar={showSnackbar} />;
      case "logs":
        return <LogsPage />;
      case "billing":
        return <BillingPage showSnackbar={showSnackbar} />;
      case "whitelabel":
        return <WhiteLabelPage showSnackbar={showSnackbar} />;
      case "settings":
        return <SettingsPage showSnackbar={showSnackbar} />;
      case "tenants":
        return <TenantsPage showSnackbar={showSnackbar} />;
      default:
        return <DashboardPage />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#0B0F19" }}>
        <Sidebar
          currentPage={currentPage}
          onPageChange={(page) => { setCurrentPage(page); setVoipPanel(null); }}
          onOpenHub={() => setHubOpen(true)}
          tenant={tenant}
        />

        <Box sx={{ flex: 1, ml: `${SIDEBAR_WIDTH}px` }}>
          {/* Top bar minimal */}
          <Box sx={{
            height: 56, display: "flex", alignItems: "center",
            justifyContent: "flex-end", px: 3,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}>
            <Box
              onClick={handleLogout}
              sx={{ cursor: "pointer", fontSize: 13, color: "#6B7280", "&:hover": { color: "#EF4444" } }}
            >
              Sair
            </Box>
          </Box>

          <Container maxWidth="lg" sx={{ py: 3 }}>
            {renderPage()}
          </Container>
        </Box>

        <HubModal
          open={hubOpen}
          onClose={() => setHubOpen(false)}
          onConnectionCreated={handleConnectionCreated}
          tenant={tenant}
          plan={plan}
        />

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default App;
